import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BoundedContent, Box, Icon, IconButton, Typography, useScreenClass } from '@vp/swan';
import { useStyles } from '@vp/ubik-context';
import styles, { stylesheet } from './Gallery.module.scss';
import ImageTile from './ImageTile';

const Gallery = () => {
    useStyles(stylesheet);
    const { t } = useTranslation();

    const [currentImageTile, setCurrentImageTile] = useState(0);
    const screenClass = useScreenClass();
    const isMobile = screenClass === 'xs';

    const cloudinaryUrl =
        'https://cms.cloudinary.vpsvc.com/image/upload/c_scale,dpr_auto,f_auto,w_auto';
    const folderPath = '/vista-logos-pages/en-US/logomaker-gen-ai/logo-gallery';

    const logoImages = [
        {
            src: `${cloudinaryUrl}${folderPath}/logo-1.webp`,
            industry: 'Media',
            altText:
                'Logo of Angel Hair Productions with a gothic skeleton holding a hanging light, setting a dark, cinematic tone.',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/logo-2.webp`,
            industry: 'Arts & Craft',
            altText:
                'Logo of Cold Porcelain Clay in soft pastel letters with a cute teddy bear, evoking playful handmade creativity.',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/logo-3.webp`,
            industry: 'Logistics',
            altText:
                'Logo of Balikbayan with a crowned cargo ship emblem, conveying trusted international shipping.',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/logo-4.webp`,
            industry: 'Agriculture',
            altText:
                "Logo of Rachel's Barn with a simple barn and warehouse icon in warm, earthy colors.",
        },
        {
            src: `${cloudinaryUrl}${folderPath}/logo-5.webp`,
            industry: 'Events',
            altText:
                'Logo of The Artful Host with a refined cocktail glass icon on deep green, suggesting elegant entertaining.',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/logo-6.webp`,
            industry: 'Wellness',
            altText:
                'Logo of Quiet Corner Labs in bold rounded type with energetic splash shapes, suggesting focus and mental clarity.',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/logo-7.webp`,
            industry: 'Fashion',
            altText:
                'Logo of 2LPZ in vibrant illustrated lettering with abstract forms, reflecting bold lifestyle branding.',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/logo-8.webp`,
            industry: 'Construction',
            altText:
                'Logo of Charter Craftsmen with a house and compass emblem, highlighting heritage craftsmanship and trust.',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/logo-9.webp`,
            industry: 'Automotive',
            altText:
                'Logo of S&K Autos with a dynamic sports car illustration, expressing speed, confidence, and reliability.',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/logo-10.webp`,
            industry: 'Childcare',
            altText:
                'Logo of Time For You with cheerful lettering and smiling character icons for friendly drop-in childcare.',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/logo-11.webp`,
            industry: 'Retail',
            altText:
                "Logo of Eyekon's Korner with playful charm icons like cherries, crown, and key, suggesting a fun boutique vibe.",
        },
        {
            src: `${cloudinaryUrl}${folderPath}/logo-12.webp`,
            industry: 'Hair Salon',
            altText:
                "Logo of Roots Afro Caribbean Hair Salon with an elegant woman's profile in a head wrap inside an arched frame.",
        },
        {
            src: `${cloudinaryUrl}${folderPath}/logo-13.webp`,
            industry: 'Events',
            altText:
                'Logo of Once Upon a Party London with flowing script, balloons, and stars for a whimsical party brand.',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/logo-14.webp`,
            industry: 'Beauty',
            altText:
                'Logo of The Willow Room Beauty Bar with soft botanical details and scissors, blending nature and styling.',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/logo-15.webp`,
            industry: 'Sustainability',
            altText:
                'Logo of Pantry Refill Exchange with a glass jar and circular arrows, symbolizing eco-friendly reuse.',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/logo-16.webp`,
            industry: 'Wellness',
            altText:
                "Logo of Moana's Witches Brew framed by floral elements in flowing script, suggesting a mystical wellness brand.",
        },
        {
            src: `${cloudinaryUrl}${folderPath}/logo-17.webp`,
            industry: 'Memorial services',
            altText:
                'Logo of Momoro with a heart-shaped leaf emblem, expressing gentle remembrance and celebration of life.',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/logo-18.webp`,
            industry: 'Photography',
            altText:
                'Logo of The Storybook Tales Photography Studio with colorful rounded letters and playful eye details.',
        },
    ];

    const imageTiles = logoImages.map((image) => ({
        src: image.src,
        altText: image.altText,
        industry: image.industry,
    }));

    const totalImageTiles = imageTiles.length;

    const nextTile = () => {
        setCurrentImageTile((prev) => (prev + 1) % totalImageTiles);
    };

    const previousTile = () => {
        setCurrentImageTile((prev) => (prev - 1 + totalImageTiles) % totalImageTiles);
    };

    const getTilePosition = (index: number) => {
        const current = currentImageTile;
        const prev = (current - 1 + totalImageTiles) % totalImageTiles;
        const next = (current + 1) % totalImageTiles;

        if (index === current) return 'center';
        if (index === prev) return 'left';
        if (index === next) return 'right';
        return 'hidden';
    };

    return (
        <BoundedContent
            as="section"
            backgroundColor="warm-white"
            id="gallery"
            py={8}
            aria-live={isMobile ? 'polite' : undefined}
            aria-roledescription={isMobile ? 'carousel' : undefined}
            aria-label={t('Logomaker GenAi Landing Page > Gallery > Aria Label.value')}
        >
            <Box padding={8} id="logomaker-genai-gallery-title">
                <Typography as="h2" textAlign={'center'} fontFamily="secondary">
                    {t('Logomaker GenAi Landing Page > Gallery > Title.value')}
                </Typography>
            </Box>
            <Box className={styles.gallery}>
                <Box className={styles.galleryLayout} as="ul" padding={0} margin={0}>
                    {imageTiles.map((image, index) => {
                        const tilePosition = getTilePosition(index);
                        const imageTileNum = index + 1;
                        return (
                            <Box
                                key={index}
                                className={styles.imageTileContainer}
                                data-position={tilePosition}
                                as="li"
                                aria-label={t(
                                    'Logomaker GenAi Landing Page > Gallery > Image Tile > Aria Label.value',
                                    {
                                        currentNumber: imageTileNum,
                                        totalNumber: totalImageTiles,
                                    },
                                )}
                                aria-roledescription={isMobile ? 'slide' : undefined}
                                aria-hidden={isMobile && tilePosition === 'hidden'}
                            >
                                <ImageTile
                                    image={image}
                                    position={tilePosition}
                                    // For mobile carousel, tiles should not be tabbable
                                    isTabbable={!isMobile}
                                />
                            </Box>
                        );
                    })}
                </Box>

                <Box className={`${styles.carouselButtonContainer} ${styles.prev}`}>
                    <IconButton
                        tooltip={t(
                            'Logomaker GenAi Landing Page > Gallery > Previous Button > Label.value',
                        )}
                        compactMode
                        onClick={previousTile}
                    >
                        <Icon iconType="chevronLeft" />
                    </IconButton>
                </Box>
                <Box className={`${styles.carouselButtonContainer} ${styles.next}`}>
                    <IconButton
                        tooltip={t(
                            'Logomaker GenAi Landing Page > Gallery > Next Button > Label.value',
                        )}
                        compactMode
                        onClick={nextTile}
                    >
                        <Icon iconType="chevronRight" />
                    </IconButton>
                </Box>
            </Box>
        </BoundedContent>
    );
};

export default Gallery;
